import { useEffect, useState } from "react";
import { head } from "../components/common/PageHeader.jsx";
import { exportButton } from "../components/common/ExportButton.jsx";
import { esc, money } from "../utils/format.js";
import { badge } from "../components/common/StatusBadge.jsx";
import { tableViews } from "../components/tables/RecordTable.jsx";

const API_BASE_URL = `https://api.praachinorganics.com`;

export function actionIcon(type) {
  const paths = {
    view: (
      <>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z"></path>

        <circle cx="12" cy="12" r="3"></circle>
      </>
    ),

    edit: <path d="m16 3 5 5-12 12-6 1 1-6Z M14 5l5 5"></path>,

    delete: (
      <path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7"></path>
    ),
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[type]}
    </svg>
  );
}

async function apiRequest(url, options = {}) {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.message || `Request failed (${response.status})`);
  }

  return response;
}

function buildQuery(filters, includePagination = true) {
  const params = new URLSearchParams();

  if (includePagination) {
    params.set("pageNumber", String(filters.pageNumber));
    params.set("pageSize", String(filters.pageSize));
  } else {
    // The backend export ignores pagination and exports all matches.
    params.set("pageNumber", "1");
    params.set("pageSize", "10");
  }

  if (filters.search.trim()) {
    params.set("search", filters.search.trim());
  }

  if (filters.paymentStatus !== "") {
    params.set("paymentStatus", filters.paymentStatus);
  }

  if (filters.fulfillmentStatus !== "") {
    params.set("fulfillmentStatus", filters.fulfillmentStatus);
  }

  if (filters.startDate) {
    params.set("startDate", filters.startDate);
  }

  if (filters.endDate) {
    params.set("endDate", filters.endDate);
  }

  return params.toString();
}

function PaymentsContent() {
  const [rows, setRows] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    paymentStatuses: {},
    fulfillmentStatuses: {},
  });

  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [fulfillmentStatus, setFulfillmentStatus] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [appliedDates, setAppliedDates] = useState({
    startDate: "",
    endDate: "",
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch status dropdown options.
  useEffect(() => {
    let active = true;

    async function loadFilterOptions() {
      try {
        const response = await apiRequest("/api/payments/filter-options");
        const data = await response.json();

        if (!active) return;

        setFilterOptions({
          paymentStatuses: data.paymentStatuses ?? {},
          fulfillmentStatuses: data.fulfillmentStatuses ?? {},
        });
      } catch (err) {
        if (active) {
          setError(err.message || "Unable to load filter options.");
        }
      }
    }

    loadFilterOptions();

    return () => {
      active = false;
    };
  }, []);

  // Fetch payments from the backend.
  useEffect(() => {
    let active = true;

    async function loadPayments() {
      setLoading(true);
      setError("");

      const filters = {
        search,
        paymentStatus,
        fulfillmentStatus,
        startDate: appliedDates.startDate,
        endDate: appliedDates.endDate,
        pageNumber,
        pageSize,
      };

      try {
        const query = buildQuery(filters);

        const response = await apiRequest(`/api/payments?${query}`);

        const data = await response.json();

        if (!active) return;

        setRows(data.items ?? []);
        setTotalRecords(data.totalRecords ?? 0);
        setTotalPages(data.totalPages ?? 0);
      } catch (err) {
        if (!active) return;

        setRows([]);
        setTotalRecords(0);
        setTotalPages(0);
        setError(err.message || "Unable to load payments.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadPayments();

    return () => {
      active = false;
    };
  }, [
    search,
    paymentStatus,
    fulfillmentStatus,
    appliedDates,
    pageNumber,
    pageSize,
  ]);

  function applyDateFilters() {
    if (startDate && endDate && startDate > endDate) {
      setError("Start date cannot be after end date.");
      return;
    }

    setError("");
    setAppliedDates({ startDate, endDate });
    setPageNumber(1);
  }

  function resetFilters() {
    setSearch("");
    setPaymentStatus("");
    setFulfillmentStatus("");
    setStartDate("");
    setEndDate("");
    setAppliedDates({ startDate: "", endDate: "" });
    setPageNumber(1);
    setPageSize(10);
    setError("");
  }

  async function handleExport() {
    try {
      setError("");

      const filters = {
        search,
        paymentStatus,
        fulfillmentStatus,
        startDate: appliedDates.startDate,
        endDate: appliedDates.endDate,
        pageNumber: 1,
        pageSize: 10,
      };

      const query = buildQuery(filters, false);

      const response = await apiRequest(`/api/payments/excel?${query}`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = `Payments_${new Date().toISOString().slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err.message || "Unable to export payments.");
    }
  }

  const paymentStatusEntries = Object.entries(filterOptions.paymentStatuses);

  const fulfillmentStatusEntries = Object.entries(
    filterOptions.fulfillmentStatuses,
  );

  const exportControl = exportButton("payments", handleExport);

  return (
    <>
      {head(
        "FINANCE",
        "Payments",
        "Transaction records linked to orders.",
        exportControl,
      )}

      <div className={"card"}>
        {/* Search and status filters */}
        <div className={"payments-toolbar"}>
          <input
            className={"payments-search"}
            type="text"
            placeholder="Search transaction or order…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPageNumber(1);
            }}
          />

          <select
            className={"payments-select"}
            value={paymentStatus}
            onChange={(event) => {
              setPaymentStatus(event.target.value);
              setPageNumber(1);
            }}
          >
            <option value="">All Payment Statuses</option>
            {paymentStatusEntries.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>

          <select
            className={"payments-select"}
            value={fulfillmentStatus}
            onChange={(event) => {
              setFulfillmentStatus(event.target.value);
              setPageNumber(1);
            }}
          >
            <option value="">All Fulfillment Statuses</option>
            {fulfillmentStatusEntries.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {/* Start date, end date, Apply and Reset */}
        <div className={"order-filters"}>
          <div className={"order-date-field"}>
            <label htmlFor="payments-start-date">Start date</label>
            <input
              id="payments-start-date"
              className={"order-date-input"}
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>

          <div className={"order-date-field"}>
            <label htmlFor="payments-end-date">End date</label>
            <input
              id="payments-end-date"
              className={"order-date-input"}
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>

          <div className={"order-date-actions"}>
            <button
              className={"order-filter-btn"}
              type="button"
              onClick={applyDateFilters}
            >
              Apply
            </button>
            <button
              className={"order-reset-btn"}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </div>

        {error && <p role="alert">{error}</p>}

        <div className={"table-wrap"}>
          <table>
            <thead>
              <tr>
                <th>{"Transaction"}</th>
                <th>{"Order"}</th>
                <th>{"Customer"}</th>
                <th>{"Amount"}</th>
                <th>{"Method"}</th>
                <th>{"Status"}</th>
                <th>{"Actions"}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className={"empty"}>
                    {"Loading payments..."}
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <b>{esc(p.transactionNumber)}</b>
                    </td>

                    <td>{esc(p.orderNumber)}</td>

                    <td>{esc(p.customerName || "Historical customer")}</td>

                    <td>{money(p.amount)}</td>

                    <td>{esc(p.method)}</td>

                    <td>{badge(p.status)}</td>

                    <td>
                      <button
                        className="table-action"
                        type="button"
                        data-nav={"payment"}
                        data-id={p.id}
                        title="View payment"
                        aria-label="View payment"
                      >
                        {actionIcon("view")}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className={"empty"}>
                    {"No matching transactions."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={"payments-pagination"}>
          <span className={"payments-pagination-info"}>
            {totalRecords === 0
              ? "Showing 0 entries"
              : `Showing ${(pageNumber - 1) * pageSize + 1} to ${Math.min(
                pageNumber * pageSize,
                totalRecords,
              )} of ${totalRecords} entries`}
          </span>

          <div className={"payments-pagination-controls"}>
            <select
              className={"payments-page-size"}
              aria-label="Entries per page"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPageNumber(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>

            <button
              className={"payments-page-btn"}
              type="button"
              disabled={pageNumber <= 1 || loading}
              onClick={() => setPageNumber((page) => page - 1)}
            >
              Previous
            </button>

            <span className={"payments-page-number"}>
              Page {pageNumber} of {totalPages}
            </span>

            <button
              className={"payments-page-btn"}
              type="button"
              disabled={pageNumber >= totalPages || loading || totalPages === 0}
              onClick={() => setPageNumber((page) => page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function payments() {
  return <PaymentsContent />;
}

export default function PaymentsPage({ recordId } = {}) {
  return tableViews(payments());
}
